import React, { useMemo, useState } from "react";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { TextField, SelectField } from "./FormPrimitives";
import { FIELDS } from "./vaultSchema";

/**
 * Generic add-entry form used by all non-Photo-ID categories (flight, hotel,
 * family, car, lounge, tsa, global entry, insurance). Fields come from
 * `FIELDS[cat.id]`.
 *
 * A field may declare `type: "select"` with `options` set to either
 *   - a literal `[{ id, label }, ...]` array, or
 *   - the sentinel string `"holders"`, which is resolved at render time
 *     to `Me + family members` so insurance / future per-person categories
 *     can pick who the entry belongs to.
 */
export default function AddEntryForm({ cat, familyMembers = [], onSave, onCancel }) {
  const fields = FIELDS[cat.id] || [];

  // Seed the draft with a sensible default for any select fields so the
  // select isn't left as the empty string (which would render as the
  // first browser-native option but never get committed to `draft`).
  const initialDraft = useMemo(() => {
    const d = {};
    fields.forEach((f) => {
      if (f.type === "select") d[f.id] = "self";
    });
    return d;
  }, [fields]);
  const [draft, setDraft] = useState(initialDraft);

  const holderOptions = useMemo(
    () => [
      { id: "self", label: "Me" },
      ...familyMembers.map((m) => ({
        id: m.id,
        label: m.name || "Family member",
      })),
    ],
    [familyMembers]
  );

  function submit(e) {
    e?.preventDefault?.();
    // "Has any" check ignores select defaults — entries with only the
    // pre-filled holder field shouldn't be saved as empty.
    const hasAny = fields.some((f) => {
      if (f.type === "select") return false;
      const v = draft[f.id];
      return v && String(v).trim();
    });
    if (!hasAny) {
      toast.error("Fill at least one field");
      return;
    }
    onSave(draft);
  }

  function resolveOptions(f) {
    if (f.options === "holders") return holderOptions;
    return Array.isArray(f.options) ? f.options : [];
  }

  return (
    <form
      onSubmit={submit}
      data-testid="vault-add-form"
      className="mt-5 bg-white rounded-2xl shadow-soft border border-navy/10 p-4 space-y-3"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="font-jakarta font-extrabold text-navy text-base">
          New {cat.label.toLowerCase()}
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
        >
          <X size={18} weight="bold" color="#0E2C54" />
        </button>
      </div>
      {fields.map((f) =>
        f.type === "select" ? (
          <SelectField
            key={f.id}
            label={f.label}
            testId={`vault-field-${f.id}`}
            value={draft[f.id] || "self"}
            options={resolveOptions(f)}
            onChange={(v) => setDraft((d) => ({ ...d, [f.id]: v }))}
            hint={
              f.options === "holders" && familyMembers.length === 0
                ? f.hint
                : null
            }
          />
        ) : (
          <TextField
            key={f.id}
            label={f.label}
            placeholder={f.placeholder}
            testId={`vault-field-${f.id}`}
            value={draft[f.id] || ""}
            onChange={(v) => setDraft((d) => ({ ...d, [f.id]: v }))}
          />
        )
      )}
      <button
        type="submit"
        data-testid="vault-save-btn"
        className="w-full bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3 shadow-soft"
      >
        Save to Vault
      </button>
    </form>
  );
}
